import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, interval, of, Subject, concat } from 'rxjs';
import { startWith, switchMap, take, takeUntil, takeWhile, catchError } from 'rxjs/operators';
import { DoodOpenCheckTransaction } from '@core/models/check.model';

import { Paths } from 'src/app/config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { PaymentAtTableStoreSelector } from '@common/selectors/payment-at-table.selector';
import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';
import { CheckTransactionKeys } from '@config/keys/check.keys';
import { ModalsService } from '@core/services/modals/modals.service';
import {
  ReceiveReceiptModalComponent,
  ReceiveReceiptModalReceiptType,
} from '@shared/modals/receive-receipt-modal/receive-receipt-modal.component';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-pat-payment-confirmation-page',
  templateUrl: './pat-payment-confirmation-page.component.html',
  styleUrls: ['./pat-payment-confirmation-page.component.scss'],
})
export class PatPaymentConfirmationPageComponent extends DestroyerBase implements OnInit {
  @Input() body: any;

  isLoading = false;
  outstandingAmount$ = this.paymentAtTableSelector.selectCheckOutstandingAmount;
  isTransactionCancelled = false;
  transaction!: DoodOpenCheckTransaction;

  // Progressive message states
  showLongerMessage = false;
  showReferenceMessage = false;
  showIssueMessage = false;
  transactionId: string | null = null;

  // Animation states
  showSecondStep = false;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routerHelper: RouterHelperService,
    private readonly paymentAtTableSelector: PaymentAtTableStoreSelector,
    private readonly paymentAtTableDispatcher: PaymentAtTableStoreDispatcher,
    private readonly onSiteLocationsService: OnSiteLocationsService,
    private readonly modalsService: ModalsService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.transactionId = this.activatedRoute.snapshot.paramMap.get('id');

    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - OnInit',
      message: {
        from: 'PatPaymentConfirmationPageComponent ngOnInit',
        target: 'getOnSiteLocationCheckTransactions$',
        currentLocation: window.location.pathname,
        params: this.activatedRoute.snapshot.params,
        queryParams: this.activatedRoute.snapshot.queryParams,
      },
    });

    this.setupProgressiveMessages();
    this.setupStepAnimation();
    this.getOnSiteLocationCheckTransactions$();
  }

  goToOverview(): void {
    this.analyticsService.log({
      level: 'debug',
      label: '#DOOD-1250 Navigate',
      message: {
        from: 'PatPaymentConfirmationPageComponent Ligne°51',
        target: [
          this.routerHelper.translateRoute(`/${Paths.PayAtTable}/${Paths.PayAtTableOverview}`),
        ],
        currentLocation: window.location.pathname,
      },
    });
    this.router.navigate([
      this.routerHelper.translateRoute(`/${Paths.PayAtTable}/${Paths.PayAtTableOverview}`),
    ]);
  }

  openModalReceipt(): void {
    this.modalsService.open(ReceiveReceiptModalComponent.handle, undefined, {
      receiptType: ReceiveReceiptModalReceiptType.checkTransaction,
      receiptId: this.transaction[CheckTransactionKeys.Id],
    });
  }

  private setupProgressiveMessages(): void {
    // Show "operation taking longer" message after 20 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showLongerMessage = true;
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowLongerMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show longer message after 20s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 20000);

    // Show reference message after 30 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showReferenceMessage = true;
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowReferenceMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show reference message after 30s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 30000);

    // Replace longer message with issue message after 60 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showIssueMessage = true;
        this.showLongerMessage = false; // Replace the previous message
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowIssueMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show issue message after 60s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 60000);
  }

  private getOnSiteLocationCheckTransactions$(): void {
    this.isLoading = true;
    const stopPolling$ = new Subject<boolean>();

    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - StartPolling',
      message: {
        from: 'PatPaymentConfirmationPageComponent getOnSiteLocationCheckTransactions$',
        target: 'Start progressive polling for transaction status',
        currentLocation: window.location.pathname,
        transactionId: this.activatedRoute.snapshot.paramMap.get('id'),
        isLoading: this.isLoading,
      },
    });

    // Progressive polling: 2s (10x), 5s (10x), 10s (unlimited)
    const fastPolling$ = interval(2000).pipe(take(10));
    const mediumPolling$ = interval(5000).pipe(take(10));
    const slowPolling$ = interval(10000);

    concat(fastPolling$, mediumPolling$, slowPolling$)
      .pipe(
        takeUntil(stopPolling$),
        startWith(0),
        switchMap(pollCount => {
          // Determine polling phase for logging
          let pollingPhase = 'fast';
          if (pollCount >= 10) pollingPhase = 'medium';
          if (pollCount >= 20) pollingPhase = 'slow';

          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - Polling',
            message: {
              from: 'PatPaymentConfirmationPageComponent progressive polling',
              target: `Poll attempt ${pollCount} (${pollingPhase} phase)`,
              currentLocation: window.location.pathname,
              isLoading: this.isLoading,
              pollCount,
              pollingPhase,
            },
          });

          return this.paymentAtTableSelector.selectCheckActiveTransaction.pipe(take(1));
        }),
        switchMap(activeTransaction => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - ActiveTransaction',
            message: {
              from: 'PatPaymentConfirmationPageComponent activeTransaction check',
              target: 'Get transaction from store or API',
              currentLocation: window.location.pathname,
              activeTransaction: activeTransaction ? 'exists' : 'null',
              activeTransactionId: activeTransaction?.id,
            },
          });

          if (!activeTransaction) {
            const id = this.activatedRoute.snapshot.paramMap.get('id');

            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - NoActiveTransaction',
              message: {
                from: 'PatPaymentConfirmationPageComponent no active transaction',
                target: 'Use ID from URL params',
                currentLocation: window.location.pathname,
                urlParamId: id,
                willCallAPI: !!id,
              },
            });

            return id
              ? this.onSiteLocationsService.getOnSiteLocationCheckTransactions$(id)
              : of(null);
          }

          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - UseActiveTransaction',
            message: {
              from: 'PatPaymentConfirmationPageComponent using active transaction',
              target: 'Get transaction from API',
              currentLocation: window.location.pathname,
              activeTransactionId: activeTransaction.id,
            },
          });

          return this.onSiteLocationsService.getOnSiteLocationCheckTransactions$(
            activeTransaction.id,
          );
        }),
        catchError(err => {
          this.analyticsService.log({
            level: 'error',
            label: 'PatPaymentConfirmationPage - APIError',
            message: {
              from: 'PatPaymentConfirmationPageComponent API error',
              target: 'Error getting transaction from API',
              currentLocation: window.location.pathname,
              error: err?.message || err?.detail || 'Unknown error',
              errorCode: err?.status || 'N/A',
              isLoading: this.isLoading,
            },
          });

          // Set isLoading to false on error to prevent infinite loading
          this.isLoading = false;
          return of(null);
        }),
        switchMap(transaction => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - TransactionReceived',
            message: {
              from: 'PatPaymentConfirmationPageComponent transaction received',
              target: 'Process transaction status',
              currentLocation: window.location.pathname,
              transactionExists: !!transaction,
              transactionStatus: transaction?.status || 'null',
              transactionId: transaction?.id,
              isLoading: this.isLoading,
            },
          });

          if (transaction && transaction.status === 'CAPTURED') {
            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - TransactionCaptured',
              message: {
                from: 'PatPaymentConfirmationPageComponent transaction captured',
                target: 'Set isLoading to false and get check details',
                currentLocation: window.location.pathname,
                transactionId: transaction.id,
                transactionStatus: transaction.status,
                isLoading: this.isLoading,
              },
            });

            this.transaction = transaction;
            const checkId = this.paymentAtTableSelector.check.id;

            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - CheckId',
              message: {
                from: 'PatPaymentConfirmationPageComponent getting check ID',
                target: 'Check ID from store',
                currentLocation: window.location.pathname,
                checkId: checkId || 'null',
                willCallCheckAPI: !!checkId,
              },
            });

            this.resetPatChooseState();
            this.resetProgressiveMessages();

            this.isLoading = false;
            return combineLatest([
              of(transaction),
              this.onSiteLocationsService.getOnSiteLocationCheck$(checkId),
            ]);
          }

          if (transaction && transaction.status === 'CANCELLED') {
            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - TransactionCancelled',
              message: {
                from: 'PatPaymentConfirmationPageComponent transaction cancelled',
                target: 'Stop polling and set cancelled flag',
                currentLocation: window.location.pathname,
                transactionId: transaction.id,
                transactionStatus: transaction.status,
                isLoading: this.isLoading,
              },
            });

            this.isTransactionCancelled = true;
            this.resetProgressiveMessages();
            this.isLoading = false; // Set to false to stop polling
            stopPolling$.next(true);
            stopPolling$.complete();
          }

          // Log when transaction is null
          if (!transaction) {
            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - NoTransaction',
              message: {
                from: 'PatPaymentConfirmationPageComponent no transaction',
                target: 'No transaction returned from API',
                currentLocation: window.location.pathname,
                isLoading: this.isLoading,
              },
            });
          }

          return combineLatest([of(transaction), of(null)]);
        }),
        takeUntil(this._destroyerRef),
        takeWhile(() => {
          const shouldContinue = this.isLoading;

          // Log polling continuation decision
          if (!shouldContinue) {
            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - PollingStopped',
              message: {
                from: 'PatPaymentConfirmationPageComponent polling stopped',
                target: 'isLoading is false, stopping polling',
                currentLocation: window.location.pathname,
                isLoading: this.isLoading,
              },
            });
          }

          return shouldContinue;
        }, true),
      )
      .subscribe({
        next: result => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - PollingResult',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling result',
              target: 'Polling subscription result',
              currentLocation: window.location.pathname,
              result: result ? 'success' : 'null',
              isLoading: this.isLoading,
            },
          });
        },
        error: err => {
          this.analyticsService.log({
            level: 'error',
            label: 'PatPaymentConfirmationPage - PollingError',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling error',
              target: 'Polling subscription error',
              currentLocation: window.location.pathname,
              error: err?.message || 'Unknown polling error',
              isLoading: this.isLoading,
            },
          });

          // Set isLoading to false on subscription error
          this.isLoading = false;
        },
        complete: () => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - PollingComplete',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling complete',
              target: 'Polling subscription completed',
              currentLocation: window.location.pathname,
              isLoading: this.isLoading,
            },
          });

          // Set isLoading to false when polling completes
          this.isLoading = false;
        },
      });
  }

  private resetPatChooseState(): void {
    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - ResetState',
      message: {
        from: 'PatPaymentConfirmationPageComponent reset state',
        target: 'Reset PAT choose state',
        currentLocation: window.location.pathname,
        isLoading: this.isLoading,
      },
    });

    this.paymentAtTableDispatcher.resetChoose();
  }

  private setupStepAnimation(): void {
    // Show second step after 800ms with smooth animation
    setTimeout(() => {
      if (this.isLoading) {
        this.showSecondStep = true;
      }
    }, 800);
  }

  private resetProgressiveMessages(): void {
    this.showLongerMessage = false;
    this.showReferenceMessage = false;
    this.showIssueMessage = false;
  }
}
